import { renderBlockIcon, renderItemIcon, ITEM_NAMES_MAP, BLOCK_NAMES } from './Hotbar.js';
import { MAX_STACK_SIZE } from '../constants.js';

const SLOT_SIZE = 44;
const GAP = 2;
const COLS = 9;

// ============================================================
// VILLAGER PROFESSIONS AND TRADE OFFERS
// ============================================================

const PROFESSIONS = {
  farmer: {
    name: 'Granjero',
    color: '#8B4513',
    offers: [
      { inputs: [{ itemId: -36, count: 20 }], output: { itemId: -16, count: 1 }, maxUses: 8 },   // 20 wheat -> 1 emerald
      { inputs: [{ itemId: -16, count: 1 }], output: { itemId: -32, count: 6 }, maxUses: 12 },    // 1 emerald -> 6 bread
      { inputs: [{ itemId: -16, count: 1 }], output: { itemId: -33, count: 4 }, maxUses: 12 },    // 1 emerald -> 4 apples
    ],
  },
  blacksmith: {
    name: 'Herrero',
    color: '#333333',
    offers: [
      { inputs: [{ itemId: -13, count: 15 }], output: { itemId: -16, count: 1 }, maxUses: 8 },   // 15 coal -> 1 emerald
      { inputs: [{ itemId: -16, count: 3 }], output: { itemId: -34, count: 1 }, maxUses: 6 },    // 3 emeralds -> iron ingot
      { inputs: [{ itemId: -16, count: 5 }], output: { itemId: -12, count: 1 }, maxUses: 3 },    // 5 emeralds -> diamond
    ],
  },
  librarian: {
    name: 'Bibliotecario',
    color: '#EEEEEE',
    offers: [
      { inputs: [{ blockId: 12, count: 4 }], output: { itemId: -16, count: 1 }, maxUses: 12 },   // 4 planks -> 1 emerald
      { inputs: [{ itemId: -16, count: 1 }], output: { blockId: 14, count: 4 }, maxUses: 12 },   // 1 emerald -> 4 glass
      { inputs: [{ itemId: -16, count: 3 }], output: { blockId: 32, count: 1 }, maxUses: 6 },    // 3 emeralds -> bookshelf
    ],
  },
  butcher: {
    name: 'Carnicero',
    color: '#CC4444',
    offers: [
      { inputs: [{ itemId: -26, count: 8 }], output: { itemId: -16, count: 1 }, maxUses: 8 },    // 8 raw beef -> 1 emerald
      { inputs: [{ itemId: -16, count: 1 }], output: { itemId: -27, count: 5 }, maxUses: 10 },   // 1 emerald -> 5 cooked beef
      { inputs: [{ itemId: -16, count: 1 }], output: { itemId: -25, count: 3 }, maxUses: 8 },    // 1 emerald -> 3 leather
    ],
  },
  cleric: {
    name: 'Cura',
    color: '#9933CC',
    offers: [
      { inputs: [{ itemId: -21, count: 4 }], output: { itemId: -16, count: 1 }, maxUses: 8 },    // 4 gunpowder -> 1 emerald
      { inputs: [{ itemId: -16, count: 1 }], output: { itemId: -14, count: 4 }, maxUses: 12 },   // 1 emerald -> 4 redstone
      { inputs: [{ itemId: -16, count: 2 }], output: { blockId: 57, count: 1 }, maxUses: 6 },    // 2 emeralds -> glowstone
    ],
  },
  armorer: {
    name: 'Armero',
    color: '#555555',
    offers: [
      { inputs: [{ itemId: -34, count: 5 }], output: { itemId: -55, count: 1 }, maxUses: 3 },   // 5 iron -> iron helmet
      { inputs: [{ itemId: -34, count: 8 }], output: { itemId: -56, count: 1 }, maxUses: 3 },   // 8 iron -> iron chestplate
      { inputs: [{ itemId: -16, count: 3 }], output: { itemId: -34, count: 2 }, maxUses: 8 },   // 3 emeralds -> 2 iron ingots
    ],
  },
  cartographer: {
    name: 'Cartógrafo',
    color: '#EECC77',
    offers: [
      { inputs: [{ itemId: -76, count: 10 }], output: { itemId: -16, count: 1 }, maxUses: 8 },  // 10 paper -> emerald
      { inputs: [{ itemId: -16, count: 2 }], output: { itemId: -77, count: 1 }, maxUses: 4 },   // 2 emeralds -> map
      { inputs: [{ itemId: -16, count: 5 }], output: { blockId: 14, count: 8 }, maxUses: 6 },   // 5 emeralds -> 8 glass
    ],
  },
  fisherman: {
    name: 'Pescador',
    color: '#4488AA',
    offers: [
      { inputs: [{ itemId: -79, count: 6 }], output: { itemId: -16, count: 1 }, maxUses: 8 },   // 6 cod -> emerald
      { inputs: [{ itemId: -16, count: 1 }], output: { itemId: -80, count: 4 }, maxUses: 8 },   // 1 emerald -> 4 cooked cod
      { inputs: [{ itemId: -16, count: 1 }], output: { itemId: -78, count: 1 }, maxUses: 4 },   // 1 emerald -> fishing rod
    ],
  },
};

/**
 * Assign a random profession to a villager entity if it doesn't have one.
 * Returns the villager's trade data.
 */
export function getVillagerTradeData(villagerEntity) {
  if (!villagerEntity._tradeData) {
    const profKeys = Object.keys(PROFESSIONS);
    const profKey = profKeys[Math.floor(Math.random() * profKeys.length)];
    const prof = PROFESSIONS[profKey];
    villagerEntity._tradeData = {
      profession: profKey,
      professionName: prof.name,
      professionColor: prof.color,
      totalTrades: 0,
      offers: prof.offers.map(o => ({
        inputs: o.inputs.map(inp => ({ ...inp })),
        output: { ...o.output },
        maxUses: o.maxUses,
        uses: 0,
      })),
    };
  }
  return villagerEntity._tradeData;
}

// ============================================================
// Helper: get item display name
// ============================================================
function _getItemDisplayName(item) {
  if (!item) return '';
  if (item.itemId !== undefined) {
    return ITEM_NAMES_MAP[item.itemId] || 'Objeto';
  }
  if (item.blockId !== undefined) {
    return BLOCK_NAMES[item.blockId] || 'Bloque';
  }
  return '';
}

// ============================================================
// TRADING SCREEN
// ============================================================

/**
 * TradingScreen - Minecraft-style villager trading UI.
 * Shows trade offer list, input/output slots, and player inventory.
 * Same visual style as ChestScreen/FurnaceScreen.
 */
export class TradingScreen {
  constructor(inventory) {
    this.inventory = inventory;
    this.isOpen = false;
    this.villagerData = null; // { professionName, professionColor, offers }
    this.selectedOffer = 0;
    this.heldItem = null;
    this._onClose = null;
    this._build();
  }

  _build() {
    // --- Overlay ---
    this.overlay = document.createElement('div');
    this.overlay.id = 'trading-overlay';
    this.overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.55); z-index: 500;
      display: none; justify-content: center; align-items: center;
      cursor: default;
    `;

    // --- Panel ---
    this.panel = document.createElement('div');
    this.panel.style.cssText = `
      background: #C6C6C6;
      border: 4px solid #2A2A2A;
      border-top-color: #FFFFFF; border-left-color: #FFFFFF;
      padding: 8px;
      font-family: 'Courier New', monospace; color: #404040;
      user-select: none; image-rendering: pixelated;
      box-shadow: 4px 4px 0 rgba(0,0,0,0.3), inset 0 0 0 2px #8B8B8B;
    `;

    // --- Title ---
    this.titleEl = document.createElement('div');
    this.titleEl.textContent = 'Aldeano';
    this.titleEl.style.cssText = 'font-size: 14px; margin-bottom: 6px; color: #404040;';
    this.panel.appendChild(this.titleEl);

    // --- Trading Area (offers list + trade slots) ---
    this.tradeArea = document.createElement('div');
    this.tradeArea.style.cssText = `
      display: flex;
      gap: 8px;
      margin-bottom: 10px;
      min-width: ${COLS * (SLOT_SIZE + GAP)}px;
    `;

    // --- Left: Offer List (scrollable) ---
    this.offerListContainer = document.createElement('div');
    this.offerListContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 2px;
      max-height: ${SLOT_SIZE * 3 + 20}px;
      overflow-y: auto;
      min-width: 120px;
      padding-right: 4px;
    `;
    this.tradeArea.appendChild(this.offerListContainer);

    // --- Right: Trade Slot Area ---
    const tradeSlotArea = document.createElement('div');
    tradeSlotArea.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1;
    `;

    // Trade slots row: [Input1] + [Input2] -> [Output]
    const tradeRow = document.createElement('div');
    tradeRow.style.cssText = `
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 8px;
    `;

    // Input slot 1 (display only -- shows what is required)
    this.inputSlot1 = this._createSlot();
    this.inputSlot1.style.cursor = 'default';
    tradeRow.appendChild(this.inputSlot1);

    // "+" label
    const plusLabel = document.createElement('div');
    plusLabel.textContent = '+';
    plusLabel.style.cssText = 'font-size: 18px; font-weight: bold; color: #555; padding: 0 2px;';
    tradeRow.appendChild(plusLabel);

    // Input slot 2 (display only)
    this.inputSlot2 = this._createSlot();
    this.inputSlot2.style.cursor = 'default';
    tradeRow.appendChild(this.inputSlot2);

    // Arrow
    const arrowEl = document.createElement('div');
    arrowEl.style.cssText = `
      width: 30px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      color: #555;
    `;
    arrowEl.textContent = '\u27A1';
    tradeRow.appendChild(arrowEl);

    // Output slot (clickable to trade)
    this.outputSlot = this._createSlot();
    this.outputSlot.style.width = (SLOT_SIZE + 8) + 'px';
    this.outputSlot.style.height = (SLOT_SIZE + 8) + 'px';
    this.outputSlot.addEventListener('click', () => this._onTrade());
    tradeRow.appendChild(this.outputSlot);

    tradeSlotArea.appendChild(tradeRow);

    // Status message (e.g. "Agotado" or trade info)
    this.statusEl = document.createElement('div');
    this.statusEl.style.cssText = 'font-size: 11px; color: #606060; text-align: center; min-height: 14px;';
    tradeSlotArea.appendChild(this.statusEl);

    this.tradeArea.appendChild(tradeSlotArea);
    this.panel.appendChild(this.tradeArea);

    // --- Separator ---
    const sep = document.createElement('div');
    sep.style.cssText = 'height: 2px; background: #8B8B8B; margin: 6px 0;';
    this.panel.appendChild(sep);

    // --- Player inventory label ---
    const invLabel = document.createElement('div');
    invLabel.textContent = 'Inventario';
    invLabel.style.cssText = 'font-size: 12px; margin-bottom: 4px; color: #606060;';
    this.panel.appendChild(invLabel);

    // --- Player storage (27 slots: unified indices 9-35) ---
    this.playerGrid = document.createElement('div');
    this.playerGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${COLS}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
      margin-bottom: 6px;
    `;
    this.playerSlots = [];
    for (let i = 0; i < 27; i++) {
      const slot = this._createSlot();
      slot.addEventListener('click', () => this._onPlayerSlotClick(i + 9));
      slot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onPlayerSlotRightClick(i + 9); });
      this.playerGrid.appendChild(slot);
      this.playerSlots.push(slot);
    }
    this.panel.appendChild(this.playerGrid);

    // --- Player hotbar (9 slots: unified indices 0-8) ---
    const hotbarSep = document.createElement('div');
    hotbarSep.style.cssText = 'height: 2px; background: #8B8B8B; margin: 4px 0;';
    this.panel.appendChild(hotbarSep);

    this.hotbarGrid = document.createElement('div');
    this.hotbarGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${COLS}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
    `;
    this.hotbarSlots = [];
    for (let i = 0; i < 9; i++) {
      const slot = this._createSlot();
      slot.addEventListener('click', () => this._onPlayerSlotClick(i));
      slot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onPlayerSlotRightClick(i); });
      this.hotbarGrid.appendChild(slot);
      this.hotbarSlots.push(slot);
    }
    this.panel.appendChild(this.hotbarGrid);

    // --- Held item cursor ---
    this.cursorEl = document.createElement('div');
    this.cursorEl.style.cssText = `
      position: fixed; pointer-events: none; z-index: 600;
      width: ${SLOT_SIZE}px; height: ${SLOT_SIZE}px;
      display: none;
    `;
    document.body.appendChild(this.cursorEl);

    this.overlay.appendChild(this.panel);
    document.body.appendChild(this.overlay);

    // Track mouse for held item
    this._onMouseMove = (e) => {
      if (!this.isOpen) return;
      this.cursorEl.style.left = (e.clientX - SLOT_SIZE / 2) + 'px';
      this.cursorEl.style.top = (e.clientY - SLOT_SIZE / 2) + 'px';
    };
    // mousemove listener added in open(), removed in close()
  }

  // ============================================================
  // SLOT CREATION (same style as ChestScreen/FurnaceScreen)
  // ============================================================

  _createSlot() {
    const slot = document.createElement('div');
    slot.style.cssText = `
      width: ${SLOT_SIZE}px; height: ${SLOT_SIZE}px;
      background: #8B8B8B;
      border: 2px solid #373737;
      border-top-color: #FFFFFF; border-left-color: #FFFFFF;
      position: relative; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
    `;
    return slot;
  }

  // ============================================================
  // ITEM HELPERS
  // ============================================================

  _itemsMatch(a, b) {
    if (!a || !b) return false;
    if (a.blockId !== undefined && b.blockId !== undefined) return a.blockId === b.blockId;
    if (a.itemId !== undefined && b.itemId !== undefined) return a.itemId === b.itemId;
    return false;
  }

  _isEmpty(item) {
    return !item || (!item.count) || (item.blockId === 0 && item.itemId === undefined);
  }

  // ============================================================
  // OPEN / CLOSE
  // ============================================================

  open(villagerData, onClose) {
    this.villagerData = villagerData;
    this._onClose = onClose;
    this.selectedOffer = 0;
    this.heldItem = null;
    this.cursorEl.style.display = 'none';
    this.isOpen = true;
    this.overlay.style.display = 'flex';
    document.addEventListener('mousemove', this._onMouseMove);
    this._render();
  }

  close() {
    // Return held item to player inventory
    if (this.heldItem) {
      if (this.heldItem.blockId !== undefined) {
        this.inventory.addBlock(this.heldItem.blockId);
      } else if (this.heldItem.itemId !== undefined) {
        this.inventory.addItem(this.heldItem.itemId, this.heldItem.count);
      }
      this.heldItem = null;
    }
    this.isOpen = false;
    this.overlay.style.display = 'none';
    this.cursorEl.style.display = 'none';
    document.removeEventListener('mousemove', this._onMouseMove);
    if (this._onClose) this._onClose();
  }

  // ============================================================
  // TRADE LOGIC
  // ============================================================

  /**
   * Count how many of a specific item the player has in their entire inventory.
   */
  _countPlayerItem(target) {
    let total = 0;
    for (let i = 0; i < 36; i++) {
      const slot = this.inventory.getUnifiedSlot(i);
      if (!slot) continue;
      if (target.itemId !== undefined && slot.itemId === target.itemId) {
        total += slot.count;
      } else if (target.blockId !== undefined && slot.blockId === target.blockId) {
        total += slot.count;
      }
    }
    return total;
  }

  /**
   * Remove a specific amount of an item from the player inventory.
   * Returns true if successful.
   */
  _removePlayerItem(target, amount) {
    let remaining = amount;
    for (let i = 0; i < 36 && remaining > 0; i++) {
      const slot = this.inventory.getUnifiedSlot(i);
      if (!slot) continue;
      let match = false;
      if (target.itemId !== undefined && slot.itemId === target.itemId) match = true;
      if (target.blockId !== undefined && slot.blockId === target.blockId) match = true;
      if (!match) continue;

      const take = Math.min(remaining, slot.count);
      slot.count -= take;
      remaining -= take;
      if (slot.count <= 0) {
        this.inventory.setUnifiedSlot(i, null);
      }
    }
    return remaining <= 0;
  }

  /**
   * Add items to the player inventory (output of a trade).
   */
  _givePlayerItem(item) {
    if (item.blockId !== undefined) {
      // For blocks, add one at a time since addBlock adds 1
      for (let i = 0; i < item.count; i++) {
        this.inventory.addBlock(item.blockId);
      }
    } else if (item.itemId !== undefined) {
      this.inventory.addItem(item.itemId, item.count);
    }
  }

  /**
   * Check if the player can afford the currently selected offer.
   */
  _canAffordOffer(offer) {
    if (!offer) return false;
    for (const input of offer.inputs) {
      const have = this._countPlayerItem(input);
      if (have < input.count) return false;
    }
    return true;
  }

  /**
   * Execute the trade: consume inputs, give output, increment uses.
   */
  _onTrade() {
    if (!this.villagerData || !this.villagerData.offers) return;
    const offer = this.villagerData.offers[this.selectedOffer];
    if (!offer) return;

    // Check if trade is exhausted
    if (offer.uses >= offer.maxUses) return;

    // Check if player has enough items
    if (!this._canAffordOffer(offer)) return;

    // Consume inputs
    for (const input of offer.inputs) {
      this._removePlayerItem(input, input.count);
    }

    // Give output
    this._givePlayerItem(offer.output);

    // Increment uses and total trades for level progression
    offer.uses++;
    if (this._tradeData) {
      this._tradeData.totalTrades = (this._tradeData.totalTrades || 0) + 1;
    }

    this._render();
  }

  // ============================================================
  // PLAYER SLOT INTERACTIONS (same as ChestScreen)
  // ============================================================

  _onPlayerSlotClick(idx) {
    const current = this.inventory.getUnifiedSlot(idx);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem });
        this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem)) {
        const space = MAX_STACK_SIZE - current.count;
        const transfer = Math.min(space, this.heldItem.count);
        current.count += transfer;
        this.heldItem.count -= transfer;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else {
        const tmp = { ...current };
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem });
        this.heldItem = tmp;
      }
    } else if (!this._isEmpty(current)) {
      this.heldItem = { ...current };
      this.inventory.setUnifiedSlot(idx, null);
    }
    this._render();
  }

  _onPlayerSlotRightClick(idx) {
    const current = this.inventory.getUnifiedSlot(idx);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem, count: 1 });
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem) && current.count < MAX_STACK_SIZE) {
        current.count++;
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      }
    } else if (!this._isEmpty(current)) {
      const half = Math.ceil(current.count / 2);
      this.heldItem = { ...current, count: half };
      current.count -= half;
      if (current.count <= 0) this.inventory.setUnifiedSlot(idx, null);
    }
    this._render();
  }

  // ============================================================
  // RENDERING
  // ============================================================

  _render() {
    if (!this.villagerData) return;

    // Villager level system: Novato (1 offer) → Experto (2, after 8 trades) → Maestro (all, after 20)
    const totalTrades = this.villagerData.totalTrades || 0;
    let levelName, maxOffers;
    if (totalTrades >= 20) { levelName = 'Maestro'; maxOffers = 99; }
    else if (totalTrades >= 8) { levelName = 'Experto'; maxOffers = 2; }
    else { levelName = 'Novato'; maxOffers = 1; }

    // Update title with level
    this.titleEl.textContent = `${this.villagerData.professionName} — ${levelName}`;
    this.titleEl.style.color = this.villagerData.professionColor || '#404040';

    // --- Render offer list (limited by level) ---
    this.offerListContainer.innerHTML = '';
    const offers = this.villagerData.offers;
    const visibleOffers = Math.min(offers.length, maxOffers);
    for (let i = 0; i < visibleOffers; i++) {
      const offer = offers[i];
      const isSelected = i === this.selectedOffer;
      const isExhausted = offer.uses >= offer.maxUses;
      const canAfford = this._canAffordOffer(offer);

      const btn = document.createElement('div');
      btn.style.cssText = `
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 6px;
        background: ${isSelected ? '#A0A0A0' : '#8B8B8B'};
        border: 2px solid ${isSelected ? '#555' : '#373737'};
        border-top-color: ${isSelected ? '#DDD' : '#FFF'};
        border-left-color: ${isSelected ? '#DDD' : '#FFF'};
        cursor: ${isExhausted ? 'not-allowed' : 'pointer'};
        opacity: ${isExhausted ? '0.5' : '1'};
        min-height: 36px;
        position: relative;
      `;

      // Show mini input icon(s) + arrow + output icon
      const inputIcons = document.createElement('span');
      inputIcons.style.cssText = 'display: flex; align-items: center; gap: 2px;';
      for (const inp of offer.inputs) {
        const iconWrap = document.createElement('span');
        iconWrap.style.cssText = 'display: flex; align-items: center; position: relative;';
        if (inp.itemId !== undefined) {
          iconWrap.innerHTML = renderItemIcon(inp.itemId, 20);
        } else {
          iconWrap.innerHTML = renderBlockIcon(inp.blockId, 20);
        }
        // Count badge
        if (inp.count > 1) {
          const badge = document.createElement('span');
          badge.textContent = inp.count;
          badge.style.cssText = `
            font-size: 9px; font-weight: bold; color: white;
            text-shadow: 1px 1px 0 #000;
            position: absolute; bottom: -1px; right: 0;
          `;
          iconWrap.appendChild(badge);
        }
        inputIcons.appendChild(iconWrap);
      }
      btn.appendChild(inputIcons);

      // Arrow
      const arrow = document.createElement('span');
      arrow.textContent = '\u2192';
      arrow.style.cssText = 'font-size: 12px; color: #555; padding: 0 2px;';
      btn.appendChild(arrow);

      // Output icon
      const outWrap = document.createElement('span');
      outWrap.style.cssText = 'display: flex; align-items: center; position: relative;';
      if (offer.output.itemId !== undefined) {
        outWrap.innerHTML = renderItemIcon(offer.output.itemId, 20);
      } else {
        outWrap.innerHTML = renderBlockIcon(offer.output.blockId, 20);
      }
      if (offer.output.count > 1) {
        const badge = document.createElement('span');
        badge.textContent = offer.output.count;
        badge.style.cssText = `
          font-size: 9px; font-weight: bold; color: white;
          text-shadow: 1px 1px 0 #000;
          position: absolute; bottom: -1px; right: 0;
        `;
        outWrap.appendChild(badge);
      }
      btn.appendChild(outWrap);

      // Exhausted cross-out
      if (isExhausted) {
        const xMark = document.createElement('span');
        xMark.textContent = 'X';
        xMark.style.cssText = `
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          font-size: 24px; font-weight: bold; color: #CC2222;
          pointer-events: none;
        `;
        btn.appendChild(xMark);
      }

      // Affordability indicator (green/red dot)
      if (!isExhausted) {
        const dot = document.createElement('span');
        dot.style.cssText = `
          width: 6px; height: 6px; border-radius: 50%;
          background: ${canAfford ? '#22CC22' : '#CC2222'};
          margin-left: auto;
          flex-shrink: 0;
        `;
        btn.appendChild(dot);
      }

      btn.addEventListener('click', () => {
        this.selectedOffer = i;
        this._render();
      });

      this.offerListContainer.appendChild(btn);
    }

    // --- Render trade slots for selected offer ---
    const selectedOffer = offers[this.selectedOffer];
    if (selectedOffer) {
      // Input slot 1
      if (selectedOffer.inputs.length >= 1) {
        this._renderSlot(this.inputSlot1, selectedOffer.inputs[0]);
      } else {
        this._renderSlot(this.inputSlot1, null);
      }

      // Input slot 2
      if (selectedOffer.inputs.length >= 2) {
        this._renderSlot(this.inputSlot2, selectedOffer.inputs[1]);
      } else {
        this._renderSlot(this.inputSlot2, null);
      }

      // Output slot
      const isExhausted = selectedOffer.uses >= selectedOffer.maxUses;
      const canAfford = this._canAffordOffer(selectedOffer);

      this._renderSlot(this.outputSlot, selectedOffer.output);

      // Style output slot based on availability
      if (isExhausted) {
        this.outputSlot.style.cursor = 'not-allowed';
        this.outputSlot.style.opacity = '0.5';
      } else if (canAfford) {
        this.outputSlot.style.cursor = 'pointer';
        this.outputSlot.style.opacity = '1';
        this.outputSlot.style.borderColor = '#22AA22';
        this.outputSlot.style.borderTopColor = '#44DD44';
        this.outputSlot.style.borderLeftColor = '#44DD44';
      } else {
        this.outputSlot.style.cursor = 'not-allowed';
        this.outputSlot.style.opacity = '0.7';
        this.outputSlot.style.borderColor = '#AA2222';
        this.outputSlot.style.borderTopColor = '#DD4444';
        this.outputSlot.style.borderLeftColor = '#DD4444';
      }

      // Status message
      if (isExhausted) {
        this.statusEl.textContent = `Agotado (${selectedOffer.uses}/${selectedOffer.maxUses})`;
        this.statusEl.style.color = '#AA2222';
      } else if (canAfford) {
        this.statusEl.textContent = `Clic en el resultado para comerciar (${selectedOffer.uses}/${selectedOffer.maxUses})`;
        this.statusEl.style.color = '#226622';
      } else {
        this.statusEl.textContent = `Faltan materiales (${selectedOffer.uses}/${selectedOffer.maxUses})`;
        this.statusEl.style.color = '#AA6622';
      }
    }

    // --- Render player storage (unified indices 9-35) ---
    for (let i = 0; i < 27; i++) {
      this._renderSlot(this.playerSlots[i], this.inventory.getUnifiedSlot(i + 9));
    }

    // --- Render hotbar (unified indices 0-8) ---
    for (let i = 0; i < 9; i++) {
      this._renderSlot(this.hotbarSlots[i], this.inventory.getUnifiedSlot(i));
    }

    // --- Render held item on cursor ---
    if (this.heldItem) {
      this.cursorEl.style.display = 'block';
      this.cursorEl.innerHTML = '';
      this._renderSlot(this.cursorEl, this.heldItem);
      this.cursorEl.style.background = 'transparent';
      this.cursorEl.style.border = 'none';
    } else {
      this.cursorEl.style.display = 'none';
    }
  }

  _renderSlot(slotEl, item) {
    slotEl.innerHTML = '';
    if (this._isEmpty(item)) return;

    // Render icon
    if (item.itemId !== undefined) {
      slotEl.innerHTML = renderItemIcon(item.itemId, 32);
    } else {
      slotEl.innerHTML = renderBlockIcon(item.blockId, 32);
    }

    if (item.count > 1) {
      const countEl = document.createElement('div');
      countEl.textContent = item.count;
      countEl.style.cssText = `
        position: absolute; bottom: 1px; right: 3px;
        font-size: 11px; font-weight: bold; color: white;
        text-shadow: 1px 1px 0 #000;
        pointer-events: none;
      `;
      slotEl.appendChild(countEl);
    }
  }
}
